import { Elements } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { useContext, useMemo, useState } from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { showPreferences } from "vanilla-cookieconsent";

import styles from "@components/account/Account.module.css";
import Button from "@components/buttons/Button";
import PackagePurchaseOptionCheckoutForm from "@components/forms/PackagePurchaseOptionCheckoutForm";
import LargeLabel from "@components/labels/LargeLabel";
import TextBlock from "@components/text/TextBlock";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import InputLabel from "@components/typography/InputLabel";
import { cookieConsentContext } from "@context/Contexts";
import { GetPackagePurchaseOptionsQuery } from "@typing/Generated";
import { minutesToHoursAndMinutes } from "@utils/timeUtils";

type Props = {
  clearCurrentPackagePurchaseOption: () => void;
  clientId: string;
  packagePurchaseOption: GetPackagePurchaseOptionsQuery["packagePurchaseOptions"][number];
};

const PackagePurchaseOptionPurchase = ({
  clearCurrentPackagePurchaseOption,
  clientId,
  packagePurchaseOption
}: Props) => {
  const [stripeFailed, setStripeFailed] = useState<boolean>(false);

  const cookieConsentStatus = useContext(cookieConsentContext);

  const stripePromise = useMemo(
    async () =>
      new Promise<Stripe | null>((resolve, _reject) => {
        if (cookieConsentStatus.acceptsPaymentCookies === true) {
          loadStripe(import.meta.env.VITE_STRIPE_API_KEY ?? "")
            .then(stripe => resolve(stripe))
            .catch(() => {
              setStripeFailed(true);
              resolve(null);
            });
        } else {
          resolve(null);
        }
      }),
    [cookieConsentStatus.acceptsPaymentCookies]
  );

  return (
    <>
      {cookieConsentStatus.acceptsPaymentCookies ? (
        <Elements stripe={stripePromise}>
          <div className={styles.account}>
            <Heading2 bold color="gray">
              <FormattedMessage
                id="pages.client.packagePurchaseOptionPurchase.hourLabel"
                values={{
                  count: minutesToHoursAndMinutes({ dropZeroMinutes: true, minutes: packagePurchaseOption.minutes })
                }}
              />
            </Heading2>
            <BodyCopy bold color="gray-500" size="small">
              <FormattedNumber currency="USD" style="currency" value={packagePurchaseOption.priceInCents / 100.0} />
            </BodyCopy>
            <div>
              <BodyCopy color="gray-500" size="small">
                <FormattedMessage
                  id={`pages.client.account.purchaseOption${packagePurchaseOption.minutes}.description`}
                />
              </BodyCopy>
            </div>

            <div className="ion-margin-top">
              <InputLabel>
                <LargeLabel data-test="billing-info">
                  <FormattedMessage id="pages.client.packagePurchaseOptionPurchase.billingInfoLabel" />
                </LargeLabel>
              </InputLabel>
            </div>

            {stripeFailed ? (
              <BodyCopy bold color="gray">
                <FormattedMessage id="pages.client.packagePurchaseOptionPurchase.stripeFailure" />
              </BodyCopy>
            ) : (
              <PackagePurchaseOptionCheckoutForm
                clearCurrentPackagePurchaseOption={clearCurrentPackagePurchaseOption}
                clientId={clientId}
                packagePurchaseOptionId={packagePurchaseOption.id}
              />
            )}
          </div>
        </Elements>
      ) : (
        <>
          <TextBlock size="large">
            <FormattedMessage id="pages.client.packagePurchaseOptionPurchase.cookiesNotAccepted" />
          </TextBlock>
          <Button onClick={showPreferences}>
            <FormattedMessage id="pages.client.packagePurchaseOptionPurchase.manageMyConsent" />
          </Button>
        </>
      )}
    </>
  );
};

export default PackagePurchaseOptionPurchase;
